import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useShallowEqualSelector } from '../../../../../hooks/redux';

import { bookingEvent } from '../../../../../utils/gtag';
import { useResourceNotes } from '../../../../../helpers/merchants/resources/helper';
import {
  CreateUserWithResvQuery,
  useAuth,
} from '../../../../../hooks/api/auth';
import { SettingCompletion } from '../../../../../interfaces/merchants/resources/books';

import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import { setMeToBookInfo } from '../../../../../redux/actions/merchants/resources/books';

import LabeledContainer from '../../../../../molecules/common/LabeledContainer';
import BooksNotes from '../../../../../molecules/merchant/resources/book/BooksNotes';
import BooksSection from '../../../../../molecules/merchant/resources/book/BooksSection';
import BooksCompleteAlert from '../../../../../molecules/merchant/resources/book/BooksCompleteAlert';
import BooksRequestAlert from '../../../../../molecules/merchant/resources/book/BooksRequestAlert';
import BooksCenteringUnderLineMessage from '../../../../../molecules/merchant/resources/book/BooksCenteringUnderLineMessage';
import BooksSchoolItemList from '../../../../../molecules/merchant/resources/book/BooksSchoolItemList';
import InputCompletionCreateUser from '../../../../../molecules/merchant/resources/book/InputCompletionCreateUser';
import { useAppDispatch } from '../../../../../redux';
import { ErrorType } from '../../../../../domain/entities/Auth';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';

interface ElementProps {
  merchantResource: { publicId: string; name: string; imageUrl?: string };
  settings: SettingCompletion;
  publicId: string;
  withExpressFlow: boolean;
  resourcePublicId: string;
}

type Props = ElementProps;

const InputCompletionSchool: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { createUserWithResv } = useAuth();
  const [errors, setErrors] = useState<ErrorType[]>();

  const { merchantResource, settings, withExpressFlow, resourcePublicId } =
    props;
  const intl = useIntl();

  const notes = useResourceNotes(settings);

  const reservationPublicId = settings.createUserWithResv!.reservationId;

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [postable, setPostable] = useState(true);

  const handleCreateUser = useCallback(
    async (query: CreateUserWithResvQuery) => {
      setPostable(false);
      createUserWithResv(query)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);

          await dispatch(setMeToBookInfo(inputs.merchantId!, resourcePublicId));
          setSubmitted(true);
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [createUserWithResv, dispatch, inputs.merchantId, resourcePublicId],
  );

  useEffect(() => {
    // Fire GTM booking_completion event
    bookingEvent({
      step: 'completion',
      reservationPublicId,
      resourcePublicId,
    });
  }, [reservationPublicId, resourcePublicId]);

  const reservations = inputs;
  const schoolTimeSlot = reservations.schoolTimeSlots![0];

  return (
    <div>
      {settings.finishedStatus === 'accepted' && <BooksCompleteAlert />}
      {settings.finishedStatus === 'pending' && <BooksRequestAlert />}
      <BooksSection>
        {settings.createUserWithResv!.available &&
          (submitted ? (
            <BooksCenteringUnderLineMessage>
              {intl.formatMessage({
                id: 'books.formsLabel.createUserSubmitted',
              })}
            </BooksCenteringUnderLineMessage>
          ) : (
            <InputCompletionCreateUser
              postable={postable}
              reservationId={settings.createUserWithResv!.reservationId}
              errors={errors}
              email={reservations.userInfo!.customerFields!.email}
              onSubmit={(query: CreateUserWithResvQuery) =>
                handleCreateUser(query)
              }
            />
          ))}
      </BooksSection>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.booksItems',
        })}
        paddingTop={true}
      >
        {schoolTimeSlot && (
          <BooksSchoolItemList
            nickname={props.publicId}
            resourceId={merchantResource.publicId}
            resourceName={merchantResource.name}
            resourceImage={merchantResource.imageUrl ?? ''}
            resourceUrl={`/${props.publicId}/${merchantResource.publicId}`}
            startTime={schoolTimeSlot.startTime}
            endTime={schoolTimeSlot.endTime}
            instructors={schoolTimeSlot.instructors.map((i) => i.name)}
            date={schoolTimeSlot.date}
            lessonName={reservations.lesson ? reservations.lesson.name : ''}
            googleCalendarTemplateUrl={
              reservations.googleCalendarTemplateUrl ?? ''
            }
          />
        )}
      </LabeledContainer>
      {!withExpressFlow && (
        <BooksSection withPadding={true} rightSide={true}>
          <RSButtonAnchor variant="outline" isBlock href={`/${props.publicId}`}>
            {intl.formatMessage({
              id: 'books.formsLabel.backToMainPage',
            })}
          </RSButtonAnchor>
        </BooksSection>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.attentionForBooking',
        })}
        contentsPadding={true}
        paddingTop={true}
      >
        <BooksNotes notes={notes} />
      </LabeledContainer>
    </div>
  );
};

export default InputCompletionSchool;
